import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle2, Clock, Loader2, ShieldCheck, UserCheck } from 'lucide-react'
import clsx from 'clsx'
import DialogShell from '@/components/crm/DialogShell'
import { dbConsoleService, type Lifecycle } from '@/services/dbConsole'
import { count } from './format'
import { archives, archivedPhrase } from './lifecycle'
import { REQUEST_POLL_MS, decisionLine } from './exportStatus'
import { ApproverPicker, AttachmentsOption } from './ApproverPicker'

const MIN_REASON = 8

/**
 * "Export this table" from the Tables view.
 *
 * Not a download button. A bulk copy of a table leaves the platform only under
 * the two-person control the Export Vault enforces: this raises the same
 * request, scoped to the one table on screen, and then follows it — a second
 * admin approves it in the Export Vault, and the requester or the approver
 * downloads it once. Nothing here can skip a step.
 */
export function TableExportDialog({
  table,
  label,
  rows,
  lifecycle,
  onClose,
}: {
  table: string
  label: string
  rows: number | null
  lifecycle: Lifecycle | null | undefined
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [reason, setReason] = useState('')
  const [approverEmail, setApproverEmail] = useState('')
  const [includeAttachments, setIncludeAttachments] = useState(false)
  const [raisedId, setRaisedId] = useState<string | null>(null)

  const list = useQuery({
    queryKey: ['db-console', 'export-requests'],
    queryFn: dbConsoleService.listExportRequests,
    // While a request raised here is open, follow it so the requester sees the
    // second admin's decision land without reloading anything.
    refetchInterval: raisedId ? REQUEST_POLL_MS : false,
    refetchIntervalInBackground: true,
  })

  const create = useMutation({
    mutationFn: () =>
      dbConsoleService.createExportRequest({
        reason: reason.trim(),
        tables: [table],
        approverEmail,
        includeAttachments: table === 'documents' && includeAttachments,
      }),
    onSuccess: (row) => {
      setRaisedId(row.id)
      void queryClient.invalidateQueries({ queryKey: ['db-console', 'export-requests'] })
    },
  })

  const exportable = list.data ? list.data.exportableTables.includes(table) : null
  const canRequest = list.data?.viewerCanRequest !== false
  const raised = raisedId ? list.data?.requests.find((request) => request.id === raisedId) ?? create.data : null
  const errorDetail = (create.error as { response?: { data?: { detail?: string } } } | null)?.response?.data?.detail

  return (
    <DialogShell label={`Request an export of ${label}`} onClose={onClose} width="lg">
      <div className="space-y-3 overflow-y-auto p-4 text-[11px] text-stone-300">
        <p className="flex items-start gap-1.5 leading-relaxed text-stone-400">
          <ShieldCheck size={12} className="mt-0.5 flex-shrink-0 text-amber-300/80" />
          Two-person control. You state why; a different admin approves it in the Export Vault; then
          the requester or the approver downloads it once, within{' '}
          {list.data?.tokenTtlMinutes ?? 30} minutes of approval.
        </p>

        <div className="rounded border border-amber-300/10 bg-black/30 px-2.5 py-2">
          <div className="text-[9px] uppercase tracking-[0.14em] text-stone-600">Scope</div>
          <div className="mt-0.5 text-stone-100">
            {label} <span className="font-mono text-[10px] text-stone-500">{table}.csv</span>
          </div>
          <div className="mt-0.5 text-[10px] text-stone-400">
            {archives(lifecycle)
              ? `Every stored row: ${count(lifecycle.liveRows)} live and ${archivedPhrase(lifecycle)}. Each row carries its archived_at, so archived rows stay identifiable in the file.`
              : rows === null
                ? 'Every stored row.'
                : `Every stored row: ${count(rows)}.`}{' '}
            Secret-bearing columns are redacted; the manifest lists which, with a SHA-256 per file.
          </div>
        </div>

        {exportable === false ? (
          <p role="alert" className="flex items-start gap-1.5 rounded border border-amber-500/25 bg-amber-500/[0.06] px-2.5 py-1.5 text-amber-200">
            <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
            This table is not exportable. Credential and user tables never leave the platform.
          </p>
        ) : !canRequest ? (
          <p role="alert" className="flex items-start gap-1.5 rounded border border-amber-500/25 bg-amber-500/[0.06] px-2.5 py-1.5 text-amber-200">
            <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
            Your role cannot raise export requests.
          </p>
        ) : raised ? (
          <div role="status" className="space-y-1 rounded border border-emerald-500/25 bg-emerald-500/[0.06] px-2.5 py-2 text-emerald-100">
            <p className="flex items-center gap-1.5">
              <CheckCircle2 size={12} /> Request <span className="font-mono">{raised.id}</span> raised.
            </p>
            <p className={clsx('flex items-center gap-1.5 text-[10px]', raised.status === 'pending' ? 'text-amber-200' : 'text-stone-200')}>
              <Clock size={10} />
              <span>
                <span className="uppercase tracking-wider">{raised.status}</span> · {decisionLine(raised)}
              </span>
            </p>
            <p className="text-[10px] text-stone-400">
              This status refreshes on its own. Approval, the one-time token and the download are in
              the Export Vault tab.
            </p>
          </div>
        ) : (
          <>
            <label className="block">
              <span className="mb-1 block text-[9px] uppercase tracking-[0.14em] text-stone-600">
                Reason (recorded permanently)
              </span>
              <textarea
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                rows={3}
                placeholder="e.g. Client data-assurance review of the deals table"
                aria-label="Reason for the export"
                className="w-full resize-none rounded border border-amber-300/10 bg-black/40 px-2 py-1.5 text-[11px] text-stone-100 placeholder:text-stone-600 focus:border-amber-300/30 focus:outline-none"
              />
            </label>
            <ApproverPicker value={approverEmail} onChange={setApproverEmail} />
            {table === 'documents' && (
              <AttachmentsOption
                checked={includeAttachments}
                onChange={setIncludeAttachments}
                limitBytes={list.data?.attachmentLimitBytes}
                notice={list.data?.attachmentsNotice}
              />
            )}
            {create.isError && (
              <p role="alert" className="text-[10px] text-red-300">
                {errorDetail || 'The request could not be raised.'}
              </p>
            )}
            <button
              type="button"
              onClick={() => create.mutate()}
              disabled={reason.trim().length < MIN_REASON || !approverEmail || create.isPending || exportable === null}
              title={
                reason.trim().length < MIN_REASON
                  ? `A reason of at least ${MIN_REASON} characters is required`
                  : !approverEmail
                    ? 'Choose who approves this export'
                    : undefined
              }
              className="flex w-full items-center justify-center gap-1.5 rounded border border-amber-300/25 bg-amber-400/[0.1] px-2 py-1.5 text-[11px] font-medium text-amber-100 transition hover:bg-amber-400/[0.16] disabled:opacity-40"
            >
              {create.isPending ? <Loader2 size={11} className="animate-spin" /> : <UserCheck size={11} />}
              Raise export request for {table}
            </button>
          </>
        )}
      </div>
    </DialogShell>
  )
}
